import type { APAADMINBaseModelsStorageBrowseResponse, StorageFolder, StorageObject } from "../../API";
import { resolveStorageUrl as resolveStorageUrlHelper } from "../../Helper/storage";
import type { BrowseState } from "./types";
import { BROWSE_PAGE_SIZE } from "./types";

export const normalizePath = (value?: string | null): string | null => {
	if (!value) {
		return null;
	}
	const sanitized = value.replace(/\\/g, "/").trim();
	if (!sanitized) {
		return null;
	}
	const trimmed = sanitized.replace(/^\/+|\/+$/g, "").replace(/\/{2,}/g, "/");
	return trimmed || null;
};

export const coerceList = <T,>(items?: Array<T | null> | null): T[] => {
	if (!Array.isArray(items)) {
		return [];
	}
	return items.filter((item): item is T => Boolean(item));
};

export const createBrowseStateFromResponse = (response?: APAADMINBaseModelsStorageBrowseResponse | null): BrowseState => ({
	folder: response?.folder ?? null,
	breadcrumbs: coerceList<StorageFolder>(response?.breadcrumbs),
	children: coerceList<StorageFolder>(response?.children),
	objects: coerceList<StorageObject>(response?.objects),
	page: response?.page ?? 1,
	pageSize: response?.pageSize ?? BROWSE_PAGE_SIZE,
	hasMore: response?.hasMore ?? false,
	totalObjects: response?.totalObjects ?? 0
});

export const isSystemKind = (kind?: string | null) => ["content", "layout"].includes((kind ?? "").toLowerCase());

export const buildPublicUrl = (key?: string | null): string | null => {
	if (!key) return null;
	const resolved = resolveStorageUrlHelper(key);
	return resolved ? resolved : null;
};

const normalizeStorageKey = (key?: string | null): string => (key ?? "").trim().replace(/^\/+/, "");

export const resolveCompressedKey = (object?: StorageObject | null): string | null => {
	const direct = normalizeStorageKey(object?.compressedKey);
	return direct || null;
};

export const resolveOriginalKey = (object?: StorageObject | null): string | null => {
	const direct = normalizeStorageKey(object?.originalKey);
	if (direct) return direct;
	const key = normalizeStorageKey(object?.key);
	return key || null;
};

export const resolveDisplayKey = (object?: StorageObject | null): string | null =>
	resolveCompressedKey(object) ?? resolveOriginalKey(object);

export const formatFileSize = (size?: number | null): string => {
	if (typeof size !== "number" || Number.isNaN(size) || size < 0) {
		return "\u2014";
	}
	const units = ["B", "KB", "MB", "GB", "TB"];
	let value = size;
	let unitIndex = 0;
	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex++;
	}
	const formatted = unitIndex === 0 ? value.toFixed(0) : value < 10 ? value.toFixed(1) : value.toFixed(0);
	return `${formatted} ${units[unitIndex]}`;
};

export const extractErrorMessage = (error: unknown): string | null => {
	if (error && typeof error === "object" && "response" in error && (error as any).response) {
		const response = (error as any).response;
		let message = response?.data?.message ?? response?.data ?? response?.statusText ?? null;
		if (typeof message === "string") {
			const truncated = message.split(" at APA.ADMIN")[0]?.trim();
			return truncated?.length ? truncated : message;
		}
		return null;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return null;
};

export const resolvePathValue = (value?: string | null) => normalizePath(value) ?? "__root__";
